import { api, ApiTags } from './base';
import { CREATE_POA_DRAFT } from './mutations/poa/create.poa.draft';
import { POA_REVOKE } from './mutations/poa/poa.revoke';
import { POA_SIGN } from './mutations/poa/poa.sign';
import {
  CorePOACreateDraftResponse,
  CorePOACreateLinkForAuthorityInput,
  CorePOACreateDraftForAuthorityResponse,
  CorePOARevokeInput,
  CorePOASignInput,
  CorePOAUpdateDraftInput,
  CorePoaPreviewRevocationInput,
  CorePoaPreviewRevocationResponse,
  CorePOACreateLinkForAuthorityResponse,
} from './mutations/poa/types';
import { UPDATE_POA_DRAFT } from './mutations/poa/update.poa.draft';
import { GET_LINK_FOR_AUTHORITY } from './queries/poa/get.link.for.authority';
import { GET_POA_DRAFT_AND_XML } from './queries/poa/get.poa.drafts.and.xml';
import { GET_POA_DRAFTS_LIST } from './queries/poa/get.poa.drafts.list';
import { GET_POA_SIGNED_REVOCATION } from './queries/poa/get.poa.signed.revocation';
import { GET_POA_SIGNED_XML } from './queries/poa/get.poa.signed.xml';
import { POA_PREVIEW_REVOCATION } from './queries/poa/poa.review.revocation';
import {
  CorePoaGetDraftForAuthorityInput,
  CorePoaGetDraftResponse,
  CorePoaGetDraftsResponse,
  CorePoaGetRetrustListResponse,
  CorePoaGetSignedResponse,
  CorePoaSignedRevocationResponse,
} from './queries/poa/types';
import { PoaDraft, PoaRetrust, PoaSigned, PoaSignedRevocation } from './types';
import { GET_POA_DRAFT_FOR_AUTHORITY } from './queries/poa/get.poa.draft.for.authority';
import { SEND_EMAIL_TO_AUTHORITY } from './mutations/poa/send.email.to.authority';
import { GET_LIST_RETRUST_POA } from './queries/poa/get.list.retrust.poa';

import {
  GetLinkForAuthorityQuery,
  GetListRetrustPoaQuery,
  GetPoaDraftForAuthorityQuery,
  GetPoaDraftsAndXmlQuery,
  GetPoaDraftsListQuery,
  GetPoaSignedRevocationQuery,
  GetPoaSignedXmlQuery,
  useGetLinkForAuthorityQuery,
  useGetListRetrustPoaQuery,
  useGetPoaDraftsAndXmlQuery,
  useGetPoaDraftsListQuery as useGeneratedPoaDraftsListQuery,
  useGetPoaSignedRevocationQuery as useGeneratedSignedRevocationQuery,
  useGetPoaSignedXmlQuery,
  useLazyGetPoaDraftForAuthorityQuery as useGeneratedLazyDraftForAuthorityQuery,
  useLazyGetPoaDraftsAndXmlQuery,
} from './generated';
import { unwrapLazyQuery, unwrapQuery } from './unwrap';

/** Список МЧД приходит конвертом `{ poaDraft, authority, representative }` — раскладываем. */
const flattenPoas = (raw: GetPoaDraftsListQuery | undefined) =>
  raw?.corePOA?.query?.poas?.map((item) => ({
    ...item.poaDraft,
    authority: item.authority,
    representative: item.representative,
  })) as PoaDraft[] | undefined;

export const useGetPoaActiveListQuery = (
  { representativeEmployeeID }: { representativeEmployeeID?: string | null },
  options?: Parameters<typeof useGeneratedPoaDraftsListQuery>[1],
) =>
  unwrapQuery(
    useGeneratedPoaDraftsListQuery(
      { status: 'active' as never, representativeEmployeeID },
      options,
    ),
    flattenPoas,
  );

export const useGetPoaArchiveListQuery = (
  ...args: Parameters<typeof useGeneratedPoaDraftsListQuery>
) =>
  // в схеме статус называется `archived`, не `archive`
  unwrapQuery(
    useGeneratedPoaDraftsListQuery({ status: 'archived' as never }, args[1]),
    flattenPoas,
  );

export const useGetPoaDraftsListQuery = (
  ...args: Parameters<typeof useGeneratedPoaDraftsListQuery>
) =>
  unwrapQuery(
    useGeneratedPoaDraftsListQuery({ status: 'draft' as never }, args[1]),
    flattenPoas,
  );

export const useGetPoaRevokedListQuery = (
  ...args: Parameters<typeof useGeneratedPoaDraftsListQuery>
) =>
  // в фильтре нет `revoked` — забираем всё и отбираем отозванные на клиенте
  unwrapQuery(
    useGeneratedPoaDraftsListQuery({}, args[1]),
    (raw: GetPoaDraftsListQuery | undefined) =>
      raw?.corePOA?.query?.poas
        ?.filter((item) => item.poaDraft?.status === 'revoked')
        ?.map((item) => ({
          ...item.poaDraft,
          authority: item.authority,
          representative: item.representative,
        })) as PoaDraft[] | undefined,
  );

const pickDraft = (raw: GetPoaDraftsAndXmlQuery | undefined) =>
  raw?.corePOA?.getDraft as PoaDraft | undefined;

export const useGetPoaDraftQuery = (
  { id, format = 'xml' }: { id: string; format?: string | null },
  options?: Parameters<typeof useGetPoaDraftsAndXmlQuery>[1],
) => unwrapQuery(useGetPoaDraftsAndXmlQuery({ id, format: format as never }, options), pickDraft);

export const useLazyGetPoaDraftQuery = (
  ...args: Parameters<typeof useLazyGetPoaDraftsAndXmlQuery>
) => unwrapLazyQuery(useLazyGetPoaDraftsAndXmlQuery(...args), pickDraft);

export const useGetPoaSignedQuery = (...args: Parameters<typeof useGetPoaSignedXmlQuery>) =>
  unwrapQuery(
    useGetPoaSignedXmlQuery(...args),
    (raw: GetPoaSignedXmlQuery | undefined) => raw?.corePOA?.getSignedById as PoaSigned | undefined,
  );

export const useGetPoaListRetrustQuery = (
  ...args: Parameters<typeof useGetListRetrustPoaQuery>
) =>
  unwrapQuery(
    useGetListRetrustPoaQuery(...args),
    (raw: GetListRetrustPoaQuery | undefined) =>
      raw?.corePOA?.listRetrustPOAs as PoaRetrust[] | undefined,
  );

export const useGetPoaSignedRevocationQuery = (
  ...args: Parameters<typeof useGeneratedSignedRevocationQuery>
) =>
  unwrapQuery(
    useGeneratedSignedRevocationQuery(...args),
    (raw: GetPoaSignedRevocationQuery | undefined) =>
      raw?.corePOA?.getSignedRevocation?.revocation as PoaSignedRevocation | undefined,
  );

export const usePoaGetLinkForAuthorityQuery = (
  ...args: Parameters<typeof useGetLinkForAuthorityQuery>
) =>
  unwrapQuery(
    useGetLinkForAuthorityQuery(...args),
    (raw: GetLinkForAuthorityQuery | undefined) => raw?.corePOA?.getLinkForAuthority,
  );

export const useLazyPoaGetDraftForAuthorityQuery = (
  ...args: Parameters<typeof useGeneratedLazyDraftForAuthorityQuery>
) =>
  unwrapLazyQuery(
    useGeneratedLazyDraftForAuthorityQuery(...args),
    (raw: GetPoaDraftForAuthorityQuery | undefined) =>
      raw?.corePOA?.getDraft as PoaDraft | undefined,
  );

export const poaApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createPoaDraft: builder.mutation<string, { inviteId?: string }>({
      query: ({ inviteId }) => ({
        document: CREATE_POA_DRAFT,
        variables: { inviteId },
      }),
      transformResponse: (rawResponse: CorePOACreateDraftResponse) =>
        rawResponse?.corePOA?.createDraft,
    }),
    updatePoaDraft: builder.mutation<void, CorePOAUpdateDraftInput>({
      query: ({ input }) => ({
        document: UPDATE_POA_DRAFT,
        variables: {
          input,
        },
      }),
    }),
    poaSign: builder.mutation<void, CorePOASignInput>({
      query: ({ id, signature, detachedSignature, cert }) => ({
        document: POA_SIGN,
        variables: {
          id,
          cert,
          signature,
          detachedSignature,
        },
      }),
      // Подпись МЧД меняет полномочия сотрудника — его карточку и список надо перечитать.
      invalidatesTags: [ApiTags.PoaList, ApiTags.Poa, ApiTags.StaffList, ApiTags.Staff],
    }),
    poaPreviewRevocation: builder.mutation<string, CorePoaPreviewRevocationInput>({
      query: ({ poaId, revokeDate, revokeReason }) => ({
        document: POA_PREVIEW_REVOCATION,
        variables: {
          poaId,
          revokeDate,
          revokeReason,
        },
      }),
      transformResponse: (rawResponse: CorePoaPreviewRevocationResponse) =>
        rawResponse?.corePOA?.previewRevocation?.content,
    }),
    poaRevoke: builder.mutation<void, CorePOARevokeInput>({
      query: ({ id, signature, detachedSignature }) => ({
        document: POA_REVOKE,
        variables: {
          id,
          signature,
          detachedSignature,
        },
      }),
      // Отзыв МЧД снимает полномочия — карточка сотрудника и список тоже устарели.
      invalidatesTags: [ApiTags.PoaList, ApiTags.Poa, ApiTags.StaffList, ApiTags.Staff],
    }),
    poaSendEmailToAuthority: builder.mutation<
      void,
      { email: string; cert: string; poaID: string }
    >({
      query: ({ email, cert, poaID }) => ({
        document: SEND_EMAIL_TO_AUTHORITY,
        variables: {
          email,
          cert,
          poaID,
        },
      }),
    }),
  }),
});

export const {
  usePoaSignMutation,
  usePoaRevokeMutation,
  useCreatePoaDraftMutation,
  useUpdatePoaDraftMutation,
  usePoaPreviewRevocationMutation,
  usePoaSendEmailToAuthorityMutation,
} = poaApi;
